import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type { IJournalService, ReverseEntryOptions } from './IJournalService';
import { JournalEndpoints } from './journalEndpoints';
import {
    mapJournalEntry,
    mapJournalEntryList,
    toCreateBody,
    toUpdateBody,
} from './journalMapping';
import type { JournalEntryWriteRequest, JournalListQuery } from './requests';
import type {
    GetJournalEntryResponse,
    JournalEntryListResponse,
    JournalEntryWriteResponse,
} from './responses';

type JournalServiceOptions = {
    baseUrl: string;
};

/**
 * HTTP client for the ledger posting engine (`/api/v1/ledger/journal-entries`).
 *
 * Every wire shape crosses through `journalMapping` — this class knows the
 * verbs, the paths and the status codes, and nothing else knows the field
 * names. See `CONTRACT.md` for what was verified against the live API and where
 * the server's behaviour is surprising.
 *
 * Errors are not interpreted here. `makeApiRequest` already lifts the bos-api
 * `{ error, code }` envelope onto `ApiResponse.error.code`, and the codes this
 * endpoint emits — `PERIOD_CLOSED`, `UNBALANCED`, `ACCOUNT_NOT_POSTABLE`,
 * `ACCOUNT_ARCHIVED`, `STALE_UPDATE`, `SOURCE_READONLY` — are branched on at
 * the call site where there is a user to explain them to.
 */
export class JournalService implements IJournalService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: JournalServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async listEntries(
        query: JournalListQuery,
    ): Promise<ApiResponse<JournalEntryListResponse>> {
        const url = JournalEndpoints.list({
            page: query.page,
            pageSize: query.pageSize,
            ...(query.status !== 'ALL' ? { status: query.status } : {}),
            // SYSTEM is a family, not an `EntrySource`, so it travels as the
            // server's `sourceGroup` (bos-api BOS-136).
            ...(query.source === 'SYSTEM' ? { sourceGroup: 'SYSTEM' } : {}),
            ...(query.source &&
            query.source !== 'ALL' &&
            query.source !== 'SYSTEM'
                ? { source: query.source }
                : {}),
            ...(query.fromDate ? { fromDate: query.fromDate } : {}),
            ...(query.toDate ? { toDate: query.toDate } : {}),
            ...(query.accountId ? { accountId: query.accountId } : {}),
        });
        const result = await makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).get(url),
        );
        return result.success
            ? { success: true, response: mapJournalEntryList(result.response) }
            : result;
    }

    /**
     * Create (no etag) or update (etag) — see `IJournalService.saveDraft` for
     * why one method covers both.
     *
     * ## The re-read after a create is not optional
     *
     * A write response carries an `etag`, but **the server does not accept it
     * on the next write** — `POST` then `PUT` with the etag the `POST` just
     * returned is always `409 STALE_UPDATE`. Only the etag from a `GET /{id}`
     * is authoritative, and that one is stable across reads.
     *
     * Re-confirmed on the 2026-07-29 deploy, the one that made the etag
     * *required* on `PUT`. That change landed; this one did not, and the two
     * together mean an update cannot be made to work any other way.
     *
     * So a create is followed by a read, and the read's entry is what callers
     * get back. Without it, the very first edit after saving a new draft would
     * fail with a stale-token error the user has no way to act on — they never
     * loaded anything stale.
     *
     * If the re-read itself fails, the created entry is returned as-is: the
     * create *did* succeed and reporting it as a failure would be a lie. The
     * etag is then the unusable one, and the next update surfaces
     * `STALE_UPDATE`, which is recoverable by reopening the entry.
     *
     * This is reported upstream as item 8 of the backend task list; delete the
     * extra round-trip once write responses return a usable token.
     */
    public async saveDraft(
        body: JournalEntryWriteRequest,
        etag?: string,
    ): Promise<ApiResponse<JournalEntryWriteResponse>> {
        const client = getAxiosClient({ baseURL: this.baseUrl });

        if (etag) {
            const updated = await makeApiRequest<unknown>(
                client.put(
                    JournalEndpoints.update(body.id),
                    toUpdateBody(body, etag),
                ),
            );
            return updated.success
                ? {
                      success: true,
                      response: mapJournalEntry(updated.response),
                  }
                : updated;
        }

        const created = await makeApiRequest<unknown>(
            client.post(JournalEndpoints.create(), toCreateBody(body)),
        );
        if (!created.success) {
            return created;
        }
        const reread = await this.getEntry(body.id);
        return reread.success
            ? reread
            : { success: true, response: mapJournalEntry(created.response) };
    }

    public async postEntry(
        id: string,
    ): Promise<ApiResponse<JournalEntryWriteResponse>> {
        const result = await makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                JournalEndpoints.post(id),
            ),
        );
        return result.success
            ? { success: true, response: mapJournalEntry(result.response) }
            : result;
    }

    public async reverseEntry(
        id: string,
        options?: ReverseEntryOptions,
    ): Promise<ApiResponse<JournalEntryWriteResponse>> {
        const result = await makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                JournalEndpoints.reverse(id),
                options ?? {},
            ),
        );
        return result.success
            ? { success: true, response: mapJournalEntry(result.response) }
            : result;
    }

    public deleteDraft(id: string): Promise<ApiResponse<void>> {
        return makeApiRequest<void>(
            getAxiosClient({ baseURL: this.baseUrl }).delete(
                JournalEndpoints.remove(id),
            ),
        );
    }

    public async getEntry(
        id: string,
        locale?: string,
    ): Promise<ApiResponse<GetJournalEntryResponse>> {
        const result = await makeApiRequest<unknown>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                JournalEndpoints.get(id, locale),
            ),
        );
        return result.success
            ? { success: true, response: mapJournalEntry(result.response) }
            : result;
    }
}
